/**
 * Chat API — Supabase (with Realtime subscriptions)
 */

import { supabase, TABLES, fromDbList } from './db';
import { ChatMessage, TaskChat } from '../types';
import { notifyNewMessage } from './notifications';

// ============================================================================
// SEND MESSAGE
// ============================================================================

export async function sendMessage(
  connectionId: string,
  senderId: string,
  senderName: string,
  message: string
): Promise<string> {
  const now = new Date().toISOString();

  // Look up the chat row
  const { data: chat } = await supabase
    .from(TABLES.TASK_CHATS)
    .select('id, task_id, task_title, participant_ids')
    .eq('id', connectionId)
    .maybeSingle();

  // Insert message
  const { data, error } = await supabase
    .from(TABLES.CHAT_MESSAGES)
    .insert({
      chat_id: connectionId,
      sender_id: senderId,
      sender_name: senderName,
      message: message.trim(),
      read: false,
      timestamp: now,
      created_at: now,
    })
    .select('id')
    .single();

  if (error) throw error;
  const msgId = (data as { id: string }).id;

  // Update chat's last_activity_at and last_message
  const lastMessage = { id: msgId, senderId, senderName, message: message.trim(), timestamp: now, read: false };
  await supabase
    .from(TABLES.TASK_CHATS)
    .update({ last_activity_at: now, last_message: lastMessage })
    .eq('id', connectionId);

  // Notify recipient
  if (chat) {
    const participantIds = (chat.participant_ids as string[]) ?? [];
    const recipientId = participantIds.find((id) => id !== senderId);
    if (recipientId) {
      await notifyNewMessage(
        recipientId, senderId, senderName,
        chat.task_id as string, chat.task_title as string,
        message.trim(), connectionId
      ).catch(console.error);
    }
  }

  return msgId;
}

// ============================================================================
// GET MESSAGES
// ============================================================================

export async function getMessages(
  connectionId: string,
  messageLimit = 50
): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from(TABLES.CHAT_MESSAGES)
    .select('*')
    .eq('chat_id', connectionId)
    .order('timestamp', { ascending: false })
    .limit(messageLimit);

  if (error) throw error;
  const messages = fromDbList<ChatMessage>(data ?? []);
  return messages.reverse();
}

// ============================================================================
// REAL-TIME
// ============================================================================

export function listenToMessages(
  connectionId: string,
  callback: (messages: ChatMessage[]) => void,
  messageLimit = 50
): () => void {
  // Initial load
  getMessages(connectionId, messageLimit).then(callback).catch(() => callback([]));

  const channel = supabase
    .channel(`chat-messages-${connectionId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: TABLES.CHAT_MESSAGES, filter: `chat_id=eq.${connectionId}` },
      async () => {
        const msgs = await getMessages(connectionId, messageLimit).catch(() => []);
        callback(msgs);
      }
    )
    .subscribe();

  return () => { void supabase.removeChannel(channel); };
}

// ============================================================================
// MESSAGE STATUS
// ============================================================================

export async function markMessagesAsRead(connectionId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from(TABLES.CHAT_MESSAGES)
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('chat_id', connectionId)
    .neq('sender_id', userId)
    .eq('read', false);
  if (error) throw error;
}

export async function getUnreadCount(connectionId: string, userId: string): Promise<number> {
  const { count, error } = await supabase
    .from(TABLES.CHAT_MESSAGES)
    .select('*', { count: 'exact', head: true })
    .eq('chat_id', connectionId)
    .neq('sender_id', userId)
    .eq('read', false);
  if (error) return 0;
  return count ?? 0;
}

// ============================================================================
// CHAT MANAGEMENT
// ============================================================================

export async function getOrCreateChat(
  connectionRequestId: string,
  taskId: string,
  taskTitle: string,
  participantIds: string[]
): Promise<string> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from(TABLES.TASK_CHATS)
    .upsert(
      {
        id: connectionRequestId,
        task_id: taskId,
        task_title: taskTitle,
        participant_ids: participantIds,
        last_activity_at: now,
        created_at: now,
        updated_at: now,
      },
      { onConflict: 'id' }
    );
  if (error) throw error;
  return connectionRequestId;
}

export async function getUserChats(userId: string): Promise<TaskChat[]> {
  const { data, error } = await supabase
    .from(TABLES.TASK_CHATS)
    .select('*')
    .contains('participant_ids', [userId])
    .order('last_activity_at', { ascending: false });

  if (error) throw error;
  return (data ?? []).map((row) => mapChatFromDb(row));
}

export function listenToUserChats(
  userId: string,
  callback: (chats: TaskChat[]) => void
): () => void {
  getUserChats(userId).then(callback).catch(() => callback([]));

  const channel = supabase
    .channel(`user-chats-${userId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: TABLES.TASK_CHATS },
      async () => {
        const chats = await getUserChats(userId).catch(() => []);
        callback(chats);
      }
    )
    .subscribe();

  return () => { void supabase.removeChannel(channel); };
}

// ── helpers ──────────────────────────────────────────────────────────────────

function mapChatFromDb(row: Record<string, unknown>): TaskChat {
  const lastMsg = row['last_message'] as Record<string, unknown> | null;
  return {
    id: row['id'] as string,
    taskId: row['task_id'] as string,
    taskTitle: (row['task_title'] as string) ?? '',
    participantIds: (row['participant_ids'] as string[]) ?? [],
    lastActivityAt: (row['last_activity_at'] as string) ?? new Date().toISOString(),
    createdAt: (row['created_at'] as string) ?? new Date().toISOString(),
    lastMessage: lastMsg
      ? {
          id: (lastMsg['id'] as string) ?? '',
          senderId: (lastMsg['sender_id'] as string) ?? (lastMsg['senderId'] as string) ?? '',
          senderName: (lastMsg['sender_name'] as string) ?? (lastMsg['senderName'] as string) ?? '',
          message: (lastMsg['message'] as string) ?? '',
          timestamp: (lastMsg['timestamp'] as string) ?? new Date().toISOString(),
          read: (lastMsg['read'] as boolean) ?? false,
        }
      : undefined,
  };
}
